// noinspection SpellCheckingInspection
import AddBoxIcon from "@mui/icons-material/AddBox";
import Box from "@mui/material/Box";
import { Theme } from "@mui/material";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import NumberBadge from "_shared/_views/badges/number-badge";
import { DialogProps } from "_shared/_views/dialogs/dialog";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import WorkAgreementQuery, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import {
  endWorkAgreement,
  getActiveWorkAgreementCount,
  getArchivedWorkAgreementCount,
  getErrorLoadingWorkAgreementById,
  getErrorLoadingWorkAgreementsByQuery,
  getIsLoadingActiveWorkAgreementCount,
  getIsLoadingArchivedWorkAgreementCount,
  getIsLoadingWorkAgreementById,
  getIsLoadingWorkAgreementsByQuery,
  getWorkAgreementById,
  getWorkAgreementQueryByInterface,
  getWorkAgreementsByQuery,
  loadActiveWorkAgreementCount,
  loadArchivedWorkAgreementCount,
  populateWorkAgreement,
  populateWorkAgreementsByQuery,
  updateWorkAgreementInterfaceQuery,
} from "work/work-agreements/_store/work-agreement-redux-slice";
import WorkAgreementDialog from "work/work-agreements/_views/dialogs/work-agreement-dialog";
import { PageTab } from "work/work-agreements/_views/work-agreements-page-tabs";
import WorkAgreement, { WorkAgreementFieldCategory } from "work/work-agreements/work-agreement";
import { WorkAgreementOrderField } from "../_api/queries/_fields/work-agreement-order-field";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Loader from "_shared/_views/loading/loader";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const RowTitle = styled(Typography)(() => ({
  fontSize: "1.1em",
  fontWeight: "600",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const columnHelper = createMRTColumnHelper<WorkAgreement>();

export default function WorkAgreementsPage() {
  const routeParams = useLoaderData();
  const activeTab: PageTab = routeParams.pageTab ? (routeParams.pageTab as PageTab) : PageTab.Active;

  const { numDialogs, openDialog, closeDialog } = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const dispatch = useAttorneyHubDispatch();

  const activeWorkAgreementCount = useSelector(getActiveWorkAgreementCount);
  const isLoadingActiveWorkAgreementCount = useSelector(getIsLoadingActiveWorkAgreementCount);
  const archivedWorkAgreementCount = useSelector(getArchivedWorkAgreementCount);
  const isLoadingArchivedWorkAgreementCount = useSelector(getIsLoadingArchivedWorkAgreementCount);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (activeWorkAgreementCount === undefined && !isLoadingActiveWorkAgreementCount) {
      dispatch(loadActiveWorkAgreementCount({ session: session }));
    }
  }, [activeWorkAgreementCount, dispatch, isLoadingActiveWorkAgreementCount, session]);
  useEffect(() => {
    if (archivedWorkAgreementCount === undefined && !isLoadingArchivedWorkAgreementCount) {
      dispatch(loadArchivedWorkAgreementCount({ session: session }));
    }
  }, [archivedWorkAgreementCount, dispatch, isLoadingArchivedWorkAgreementCount, session]);

  const workAgreementQuery = useSelector(getWorkAgreementQueryByInterface("workAgreementIndex"));
  const defaultQuery = WorkAgreementQuery.create({
    filterParameters: WorkAgreementFilterParameters.fromSearchParams({
      searchParams,
      overrides: {
        isArchived: activeTab === PageTab.Archived,
      },
    }),
    orderParameters: WorkAgreementOrderParameters.fromSearchParams(searchParams),
    paginationParameters: PaginationParameters.fromSearchParams(searchParams),
  });
  if (!workAgreementQuery)
    dispatch(
      updateWorkAgreementInterfaceQuery({
        query: defaultQuery,
        interfaceName: "workAgreementIndex",
      }),
    );
  const paginatedWorkAgreements = useSelector(getWorkAgreementsByQuery(workAgreementQuery ?? defaultQuery));
  const isLoadingQueryFromStore = useSelector(getIsLoadingWorkAgreementsByQuery(workAgreementQuery));
  const errorLoadingQuery = useSelector(getErrorLoadingWorkAgreementsByQuery(workAgreementQuery));
  if (workAgreementQuery && !isLoadingQueryFromStore && !errorLoadingQuery && paginatedWorkAgreements === undefined) {
    dispatch(populateWorkAgreementsByQuery({ query: workAgreementQuery, session }));
  }

  const workAgreementId = React.useMemo(
    () => (routeParams.id ? new Guid(routeParams.id) : undefined),
    [routeParams.id],
  );
  const dialogWorkAgreement = useSelector(getWorkAgreementById(workAgreementId));

  const isLoadingWorkAgreement = useSelector(getIsLoadingWorkAgreementById(workAgreementId));
  const errorLoadingWorkAgreement = useSelector(getErrorLoadingWorkAgreementById(workAgreementId));

  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>(PaginationParameters.DEFAULT);

  const redirectForWorkAgreementIfNecessary = React.useCallback(
    (action: string, pageTab: PageTab) => {
      if (!dialogWorkAgreement) return;
      let redirectAction: string | undefined = undefined;

      if (dialogWorkAgreement.isArchived && action !== "view") {
        redirectAction = "view";
      }

      let redirectPageTab: PageTab | undefined = undefined;

      if (dialogWorkAgreement.isArchived && pageTab !== PageTab.Archived) {
        redirectPageTab = PageTab.Archived;
      }

      if (redirectAction || redirectPageTab) {
        const newPath = `/work/${redirectPageTab ?? pageTab}/${redirectAction ?? action}/${workAgreementId}`;
        navigate(newPath, { replace: true });
      }
    },
    [dialogWorkAgreement, navigate, workAgreementId],
  );

  const handleOpenWorkAgreementDialog = React.useCallback(
    (title: string, workAgreementId: Guid, readOnly?: boolean) => {
      const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);

      const dialogProps: DialogProps = {
        title: title,
        titleStyle: { paddingBottom: 0 },
        MuiProps: { fullWidth: false, maxWidth: "xl" },
        contentSxProps: { pb: { xs: 0 } },
        resetStack: true,
        component: <WorkAgreementDialog workAgreementId={workAgreementId} readOnly={readOnly} />,
        onClose: () => {
          closeDialog();
          navigate(parentPath.pathname);
        },
      };

      openDialog(dialogProps);
    },
    [closeDialog, navigate, openDialog],
  );

  const dispatchWorkAgreementQuery = React.useCallback(() => {
    const newQuery = WorkAgreementQuery.create({
      filterParameters: WorkAgreementFilterParameters.fromSearchParams({
        searchParams,
        overrides: {
          isArchived: activeTab === PageTab.Archived,
        },
      }),
      orderParameters: WorkAgreementOrderParameters.fromSearchParams(searchParams),
      paginationParameters: PaginationParameters.fromSearchParams(searchParams),
    });

    dispatch(
      updateWorkAgreementInterfaceQuery({
        interfaceName: "workAgreementIndex",
        query: newQuery,
      }),
    );
  }, [activeTab, dispatch, searchParams]);

  const openWorkAgreementDialog = React.useCallback(
    (action: string, _tab: WorkAgreementFieldCategory) => {
      if (!workAgreementId || !session.user || !action) {
        console.warn("Unable to open work agreement dialog. Missing required parameters.");
        return;
      }

      handleOpenWorkAgreementDialog("Work Agreement", workAgreementId, dialogWorkAgreement?.isArchived);
    },
    [workAgreementId, session.user, handleOpenWorkAgreementDialog, dialogWorkAgreement?.isArchived],
  );

  // Handle URL changes
  useEffect(() => {
    if (!routeParams.pageTab) {
      navigate(`/work/${PageTab.Active}`);
    }
  }, [navigate, routeParams]);

  useEffect(() => {
    dispatchWorkAgreementQuery();
  }, [activeTab, dispatchWorkAgreementQuery, routeParams, searchParams]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (globalFilter) {
      newSearchParams.set("query", globalFilter);
    }

    const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
    if (nameFilter) {
      newSearchParams.set("name", nameFilter);
    }

    const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
    if (descriptionFilter) {
      newSearchParams.set("description", descriptionFilter);
    }

    const orderingField = sorting.find((sort) =>
      Object.values(WorkAgreementOrderField)
        .map((field) => field.toLowerCase())
        .includes(sort.id.toLowerCase()),
    );

    if (orderingField) {
      newSearchParams.set("orderBy", orderingField.id);
      newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
    }

    const paginationPageIndex = pagination.pageIndex ?? 0;
    newSearchParams.set("page", (paginationPageIndex + 1).toString());
    const paginationPageSize = pagination.pageSize ?? 10;
    newSearchParams.set("pageSize", paginationPageSize.toString());

    setSearchParams(newSearchParams);
  }, [globalFilter, columnFilters, sorting, pagination, setSearchParams]);

  useEffect(() => {
    if (errorLoadingQuery) {
      enqueueSnackbar("Error loading work agreement table", {
        variant: "error",
      });
    }
    if (dialogWorkAgreement === null) {
      enqueueSnackbar("Work agreement not found", { variant: "warning" });
      navigate(`/work/${activeTab}`);
    } else if (errorLoadingWorkAgreement) {
      enqueueSnackbar("Error loading work agreement", { variant: "error" });
      navigate(`/work/${activeTab}`);
    }
  }, [errorLoadingQuery, errorLoadingWorkAgreement, dialogWorkAgreement, navigate, activeTab]);

  useEffect(() => {
    if (session.user?.id && workAgreementId && dialogWorkAgreement) {
      const pageTab = routeParams.pageTab as PageTab;
      const action = routeParams.action as string;

      redirectForWorkAgreementIfNecessary(action, pageTab);
    }
  }, [
    routeParams,
    workAgreementId?.value,
    dialogWorkAgreement?.id,
    session.user?.id,
    workAgreementId,
    dialogWorkAgreement,
    redirectForWorkAgreementIfNecessary,
  ]);

  useEffect(() => {
    if (!session.user) return;
    if (workAgreementId && dialogWorkAgreement === undefined && !isLoadingWorkAgreement && !errorLoadingWorkAgreement) {
      dispatch(populateWorkAgreement({ session, id: workAgreementId }));
    } else if (!(workAgreementId && !dialogWorkAgreement)) {
      const dialogTab = WorkAgreementFieldCategory[routeParams.dialogTab as keyof typeof WorkAgreementFieldCategory];
      const action = routeParams.action as string;
      if (!action) return;
      if (numDialogs === 0) {
        openWorkAgreementDialog(action, dialogTab);
      }
    }
  }, [
    workAgreementId?.value,
    dialogWorkAgreement,
    dialogWorkAgreement?.id?.value,
    isLoadingWorkAgreement,
    routeParams,
    session,
    workAgreementId,
    errorLoadingWorkAgreement,
    dispatch,
    numDialogs,
    openWorkAgreementDialog,
  ]);

  function renderNameColumn(work: WorkAgreement) {
    if (!work.name) return <Icon>remove</Icon>;
    return <RowTitle>{work.name.value}</RowTitle>;
  }

  function renderDescriptionColumn(work: WorkAgreement) {
    if (!work.description) return <Icon>remove</Icon>;
    return work.description.value;
  }

  const columns = useMemo(() => {
    const returnedColumns = [
      columnHelper.accessor(renderNameColumn, {
        header: "Name",
        id: "name",
      }),
      columnHelper.accessor(renderDescriptionColumn, {
        header: "Description",
        id: "description",
        enableSorting: false,
      }),
      columnHelper.accessor((work) => work.hireDate?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>, {
        header: "Hire Date",
        id: "hireDate",
        enableSorting: false,
      }),
      columnHelper.accessor((work) => work.startDate?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>, {
        header: "Start Date",
        id: "start",
      }),
      columnHelper.accessor((work) => work.endDate?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>, {
        header: "End Date",
        id: "end",
      }),
    ];
    if (activeTab === PageTab.Archived) {
      returnedColumns.push(
        columnHelper.accessor((work) => work.archiveDate?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>, {
          header: "Archive Date",
          id: "archiveDate",
        }),
      );
    }
    return returnedColumns;
  }, [activeTab]);

  const handleReviseWorkAgreement = useCallback(
    (workAgreement: WorkAgreement) => {
      if (!workAgreement?.id) {
        console.error("Work agreement not found");
        return;
      }
      closeDialog();
      navigate("/proposals/drafts/create", {
        state: {
          workAgreement: workAgreement.toJSON(),
        },
      });
    },
    [closeDialog, navigate],
  );

  const handleEndWorkAgreement = useCallback(
    async (workAgreement: WorkAgreement) => {
      if (!workAgreement?.id) {
        console.error("Work agreement not found");
        return;
      }

      const endResponse = await confirm({
        title: "End Work Agreement?",
        message:
          "This will void the work agreement for any future work and only work performed " +
          "before this date will be billable until a new proposal is agreed to.",
        okButtonText: "End",
      });

      if (endResponse === ConfirmResponse.Cancel) return;

      try {
        dispatch(endWorkAgreement({ session, id: workAgreement.id }));
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Failed to end work agreement", { variant: "error" });
      }
    },
    [confirm, dispatch, session],
  );

  const getMenuItems = useCallback(
    (workAgreement: WorkAgreement): React.ReactNode[] => {
      return [
        <MenuItem key="view" onClick={() => navigate(`/work/${activeTab}/view/${workAgreement.id}`)}>
          <ListItemIcon>
            <Icon>visibility</Icon>
          </ListItemIcon>
          <ListItemText primary="View" />
        </MenuItem>,
        <MenuItem
          key="revise"
          disabled={workAgreement.isArchived}
          onClick={() => handleReviseWorkAgreement(workAgreement)}
        >
          <ListItemIcon>
            <Icon>edit</Icon>
          </ListItemIcon>
          <ListItemText primary="Revise" />
        </MenuItem>,
        <MenuItem
          key="cancel"
          disabled={workAgreement.isArchived}
          onClick={() => handleEndWorkAgreement(workAgreement)}
        >
          <ListItemIcon>
            <Icon color="error">cancel</Icon>
          </ListItemIcon>
          <ListItemText primary="End Agreement" />
        </MenuItem>,
      ];
    },
    [navigate, activeTab, handleEndWorkAgreement, handleReviseWorkAgreement],
  );

  const openWorkAgreement = useCallback(
    (workAgreement: WorkAgreement) => {
      if (!workAgreement.id) {
        console.error("Work Agreement ID is required");
        return;
      }
      navigate(`/work/${activeTab}/view/${workAgreement.id}`);
    },
    [navigate, activeTab],
  );

  const tableOptions: MRT_TableOptions<WorkAgreement> = useMemo(() => {
    return {
      columns,
      data: paginatedWorkAgreements?.data ?? [],
      enableRowSelection: false,
      enableRowActions: true,
      enableTableHead: true,
      muiTableBodyRowProps: ({ row }) => ({
        onClick: () => openWorkAgreement(row.original),
        sx: { cursor: "pointer" },
      }),
      renderToolbarInternalActions: ({ table }) => (
        <>
          {!isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
              <Button startIcon={<AddBoxIcon />} onClick={() => navigate("/proposals/drafts/create")}>
                Create Proposal
              </Button>
            </ToolbarButtonsContainer>
          )}
          {isMediumDisplaySize && (
            <NewButton color="primary" onClick={() => navigate("/proposals/drafts/create")}>
              <AddBoxIcon fontSize="large" />
            </NewButton>
          )}
        </>
      ),
      renderTopToolbarCustomActions: ({ table }) => {
        return (
          <TableTopToolbarContainer>
            <TableTitle variant="h4">Work</TableTitle>
            <LowerTopToolbarContainer>
              <Tabs
                value={activeTab}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_event, newValue) => navigate(`/work/${newValue}`)}
              >
                <Tab
                  value={PageTab.Active}
                  label={
                    <NumberBadge
                      tooltip={`${activeWorkAgreementCount ?? 0} active work agreement(s)`}
                      badgeCount={activeWorkAgreementCount ?? 0}
                      color={"info"}
                      text={PageTab.Active}
                    />
                  }
                />
                <Tab
                  value={PageTab.Archived}
                  label={
                    <NumberBadge
                      tooltip={`${archivedWorkAgreementCount ?? 0} archived work agreement(s)`}
                      badgeCount={archivedWorkAgreementCount ?? 0}
                      color={"info"}
                      text={PageTab.Archived}
                    />
                  }
                />
              </Tabs>
            </LowerTopToolbarContainer>
            {isMediumDisplaySize && (
              <ToolbarButtonsContainer>
                <ToolbarButtons>
                  <ToggleGlobalFilterButton table={table} />
                  <ToggleFiltersButton table={table} />
                  <ShowHideColumnsButton table={table} />
                  <ToggleDensePaddingButton table={table} />
                  <ToggleFullScreenButton table={table} />
                </ToolbarButtons>
              </ToolbarButtonsContainer>
            )}
          </TableTopToolbarContainer>
        );
      },
      getRowId: (row) => row.id?.value ?? Guid.generate().value,
      renderRowActionMenuItems: ({ row }) => getMenuItems(row.original),
      enableColumnPinning: true,
      initialState: {
        columnPinning: { right: ["mrt-row-actions"] },
      },
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      muiTableContainerProps: ({ table }) => ({
        sx: {
          height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
        },
      }),
      muiTablePaperProps: {
        sx: {
          height: "100%",
        },
      },
      muiToolbarAlertBannerProps: errorLoadingQuery
        ? {
            color: "error",
            children: "Error loading data",
          }
        : {
            action: <AlertActions></AlertActions>,
          },
      rowCount: paginatedWorkAgreements?.totalElements ?? 0,
      pageCount: Math.ceil((paginatedWorkAgreements?.totalElements ?? 0) / pagination.pageSize),
      enablePagination: true,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      state: {
        columnFilters,
        globalFilter,
        isLoading: isLoadingQueryFromStore,
        pagination,
        showAlertBanner: Boolean(errorLoadingQuery),
        showProgressBars: isLoadingQueryFromStore,
        sorting,
        rowSelection,
      },
    } satisfies MRT_TableOptions<WorkAgreement>;
  }, [
    columns,
    paginatedWorkAgreements?.data,
    paginatedWorkAgreements?.totalElements,
    errorLoadingQuery,
    pagination,
    columnFilters,
    globalFilter,
    isLoadingQueryFromStore,
    sorting,
    rowSelection,
    openWorkAgreement,
    isMediumDisplaySize,
    navigate,
    activeTab,
    activeWorkAgreementCount,
    archivedWorkAgreementCount,
    getMenuItems,
  ]);

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToWork) return <NotAuthorized />;

  return <MRTNoMemo<WorkAgreement> options={tableOptions} />;
}
